import { useRef, useState } from 'react'
import { cardKey } from './cardPick'
import { formatLatLon } from './coords'
import { useT } from '../i18n'
import { MapCallout } from '../map/MapCallout'
import { trackPointCaption } from './trackPointCaption'
import { catchGradeCard } from './catchGrade'

interface TrackPointCardProps {
  /** Точка на карте: к ней карточка и привязана (см. MapCallout). */
  lat: number
  lon: number
  /** Дата, выбранная ползунком дней. */
  date: string
  /** Сегодня — дата якорного маркера трека (роль 'today'). */
  today: string
  /** Позиции на выбранный день нет — показана последняя известная. */
  frozen: boolean
  /** Донный вид: течением не сносится (статус 'static'). Тогда «последняя
   *  известная позиция» — неверная подпись: позиция не устарела, она такая. */
  static?: boolean
  source: string
  /** Свойства фичи: из них берётся градация улова и её объяснение. */
  props?: Record<string, unknown>
  /** Закреплена кликом: ловит мышь, показывает крестик и кнопку копирования.
   *  Незакреплённая (читалка при наведении) прозрачна для мыши — иначе, наползая
   *  на курсор, она уводила бы его с точки и гасила сама себя. */
  pinned?: boolean
  onClose: () => void
}

/** Координаты снесённой точки — чтобы перенести их в навигатор.
 *
 *  Отдельная карточка, а не форма правки: правят ОТМЕТКУ (где косяк увидели),
 *  а здесь расчётное положение на дату — редактировать в нём нечего. */
export function TrackPointCard({
  lat,
  lon,
  date,
  today,
  frozen,
  static: isStatic = false,
  source,
  props,
  pinned = true,
  onClose,
}: TrackPointCardProps) {
  const [copied, setCopied] = useState(false)
  const t = useT()
  const text = formatLatLon(lat, lon)
  // Карточка одна на все точки и больше не пересоздаётся при переходе курсора
  // (ключа у неё нет — см. cardPick.ts), поэтому «Скопировано» надо снимать
  // самим: иначе отметка о копировании перешла бы на чужие координаты.
  const identity = cardKey({ lng: lon, lat, props })
  const shown = useRef(identity)
  if (shown.current !== identity) {
    shown.current = identity
    if (copied) setCopied(false)
  }
  const grade = catchGradeCard({ source, ...(props ?? {}) })
  const caption = trackPointCaption(props ?? {}, date, today, { static: isStatic, frozen })

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Буфер недоступен (нет https / отказ в правах) — цифры на экране есть,
      // их можно выделить руками. Молча не притворяемся, что скопировали.
      setCopied(false)
    }
  }

  return (
    // Фон непрозрачный (bg-deep в MapCallout): карточка висит над пёстрой
    // картой, и сквозь полупрозрачный просвечивали течения — подписи теряли
    // контраст.
    <MapCallout anchor={{ lng: lon, lat }} width={256} pinned={pinned}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-foam">
          {source === 'catch' ? t('models.trackPoint.catch') : t('models.cluster.title')}
        </div>
        {pinned && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-mist transition-colors hover:bg-haze hover:text-foam"
          >
            ×
          </button>
        )}
      </div>

      {/* Что это за точка: у трека их три (отметка / сегодня / выбранный день),
          координаты разные — и дата обязана отличаться так же. */}
      <div className="mt-0.5 text-[11px] text-mist">
        {t(caption.key, caption.vars)}
      </div>

      {grade && (
        // Одно слово: градация — доля от того, что эта единица берёт в лучшие
        // дни. Тонны и разбор эталона с карточки убраны (см. catchGrade.ts).
        <div className="mt-2 rounded-lg border border-hull bg-trench px-2.5 py-1.5">
          <span className="text-sm font-semibold text-foam">{t(grade.titleKey)}</span>
        </div>
      )}

      <div className="tnum mt-2 select-all rounded-lg border border-hull bg-trench px-2.5 py-1.5 text-sm font-medium text-foam">
        {text}
      </div>

      {pinned ? (
        <button
          type="button"
          onClick={copy}
          className="mt-2 w-full rounded-lg border border-hull px-3 py-1.5 text-xs text-foam transition-colors hover:border-sonar hover:bg-haze"
        >
          {copied ? t('common.copied') : t('common.copy')}
        </button>
      ) : (
        // Читалка мышь не ловит — кнопку копирования не показываем: она была бы
        // видна, но не нажималась. Клик по точке закрепит карточку с кнопкой.
        <div className="mt-2 text-[11px] text-faint">{t('models.trackPoint.clickToPin')}</div>
      )}
    </MapCallout>
  )
}
